/**
 * Cart variant edit modal (adapted from autel-shop).
 */
const MOBILE_TITLE_MQ = window.matchMedia('(max-width: 767px)');

function getCartTitleStrings() {
  return (
    (window.Theme && window.Theme.cartEditStrings) || {
      addToCart: 'Add to cart',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      expandProductTitle: 'Show full product title',
      collapseProductTitle: 'Hide product title',
    }
  );
}

function syncCollapsibleTitleWrap(wrap, title, toggle) {
  const strings = getCartTitleStrings();

  wrap.classList.remove('is-expanded', 'is-collapsible');
  toggle.hidden = true;
  toggle.setAttribute('aria-expanded', 'false');
  toggle.setAttribute('aria-label', strings.expandProductTitle);

  if (!MOBILE_TITLE_MQ.matches) return;

  wrap.classList.add('is-collapsible');
  const needsToggle = title.scrollHeight > title.clientHeight + 1;
  if (!needsToggle) {
    wrap.classList.remove('is-collapsible');
    return;
  }

  toggle.hidden = false;
}

function bindCollapsibleTitleWrap(wrap) {
  if (wrap.dataset.collapsibleTitleBound === 'true') return;

  const title = wrap.querySelector('[data-cart-edit-product-title], [data-cart-item-product-title]');
  const toggle = wrap.querySelector('[data-cart-edit-title-toggle], [data-cart-item-title-toggle]');
  if (!title || !toggle) return;

  const strings = getCartTitleStrings();

  const sync = () => syncCollapsibleTitleWrap(wrap, title, toggle);

  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    const expanded = !wrap.classList.contains('is-expanded');
    wrap.classList.toggle('is-expanded', expanded);
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.setAttribute(
      'aria-label',
      expanded ? strings.collapseProductTitle : strings.expandProductTitle
    );
  });

  wrap._collapsibleTitleSync = sync;
  wrap.dataset.collapsibleTitleBound = 'true';
  sync();
  window.requestAnimationFrame(sync);
}

function initCollapsibleTitles(root = document) {
  root.querySelectorAll('[data-cart-edit-title-wrap], [data-cart-item-title-wrap]').forEach((wrap) => {
    bindCollapsibleTitleWrap(wrap);
  });
}

function refreshCollapsibleTitles() {
  document.querySelectorAll('[data-cart-edit-title-wrap], [data-cart-item-title-wrap]').forEach((wrap) => {
    if (typeof wrap._collapsibleTitleSync === 'function') {
      wrap._collapsibleTitleSync();
    }
  });
}

function resetCollapsibleTitlesInList() {
  const list = document.querySelector('[data-cart-item-list]');
  if (!list) return;

  list.querySelectorAll('[data-cart-item-title-wrap]').forEach((wrap) => {
    delete wrap.dataset.collapsibleTitleBound;
    delete wrap._collapsibleTitleSync;
  });
}

class CartEdit {
  constructor() {
    this.modal = document.getElementById('Cart-Edit-Modal');
    if (!this.modal) return;

    this.content = this.modal.querySelector('[data-cart-edit-content]');
    this.lineIndex = null;
    this.quantity = 1;
    this.variants = [];
    this.selectedOptions = [];
    this.strings = (window.Theme && window.Theme.cartEditStrings) || {
      addToCart: 'Add to cart',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      expandProductTitle: 'Show full product title',
      collapseProductTitle: 'Hide product title',
    };
    this.routes = (window.Theme && window.Theme.routes) || {};

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
        this.close();
      }
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-edit]');
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-edit-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  open(trigger) {
    if (trigger.classList.contains('loading')) return;

    const url = trigger.dataset.url;
    this.lineIndex = trigger.dataset.line;
    this.quantity = parseInt(trigger.dataset.quantity, 10) || 1;

    if (!url) return;

    trigger.classList.add('loading');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const template = doc.querySelector('[data-cart-edit-template]');
        if (!template) throw new Error('Cart edit template missing');

        this.content.innerHTML = template.outerHTML;
        this.bindTemplate();
        this.modal.removeAttribute('hidden');
        document.body.classList.add('cart-edit-open');
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        trigger.classList.remove('loading');
      });
  }

  close() {
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('cart-edit-open');
    this.content.innerHTML = '';
  }

  bindTemplate() {
    const root = this.content.querySelector('[data-cart-edit-template]');
    const form = root.querySelector('[data-cart-edit-form]');
    const qtyInput = root.querySelector('[data-cart-edit-quantity]');
    const variantsScript = root.querySelector('[data-cart-edit-variants]');

    if (qtyInput) qtyInput.value = this.quantity;

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.selectedOptions = [];
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const checked = swatch.querySelector('input:checked');
      this.selectedOptions[parseInt(swatch.dataset.optionIndex, 10)] = checked ? checked.value : null;
    });

    root.querySelectorAll('[data-cart-edit-option]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = parseInt(input.dataset.optionPosition, 10);
        this.selectedOptions[position] = input.value;
        const header = input.closest('.cart-edit__swatch').querySelector('[data-header-option]');
        if (header) header.textContent = input.value;
        this.updateVariantUI(root);
      });
    });

    this.updateVariantUI(root);
    const titleWrap = root.querySelector('[data-cart-edit-title-wrap]');
    if (titleWrap) {
      delete titleWrap.dataset.collapsibleTitleBound;
      delete titleWrap._collapsibleTitleSync;
      bindCollapsibleTitleWrap(titleWrap);
    }
    form.addEventListener('submit', (event) => this.onSubmit(event, root));
  }

  findVariant() {
    return this.variants.find((variant) => {
      return this.selectedOptions.every((value, index) => {
        if (value == null) return true;
        return variant[`option${index + 1}`] === value;
      });
    });
  }

  updateVariantUI(root) {
    const variant = this.findVariant();
    const variantInput = root.querySelector('[data-cart-edit-variant-id]');
    const priceEl = root.querySelector('[data-cart-edit-price]');
    const compareEl = root.querySelector('[data-cart-edit-compare]');
    const labelEl = root.querySelector('[data-cart-edit-option-label]');
    const imageEl = root.querySelector('[data-cart-edit-image]');
    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    const submitLabel = submitBtn && submitBtn.querySelector('span:first-child');

    this.disableUnavailableOptions(root);

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        if (submitLabel) submitLabel.textContent = this.strings.unavailable;
      }
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price_formatted;
    if (labelEl) labelEl.textContent = variant.title;

    if (compareEl) {
      if (variant.compare_at_price_formatted) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.classList.remove('hide');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hide');
      }
    }

    if (imageEl && variant.featured_image) {
      imageEl.src = variant.featured_image;
    }

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      if (submitLabel) {
        submitLabel.textContent = variant.available ? this.strings.addToCart : this.strings.soldOut;
      }
    }
  }

  disableUnavailableOptions(root) {
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      swatch.querySelectorAll('.cart-edit__swatch-element').forEach((element) => {
        const value = element.dataset.value;
        const available = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return this.selectedOptions.every((selected, index) => {
            if (index === optionIndex || selected == null) return true;
            return variant[`option${index + 1}`] === selected;
          });
        });

        element.classList.toggle('soldout', !available);
        const input = element.querySelector('input');
        if (input) input.disabled = !available;
      });
    });
  }

  onSubmit(event, root) {
    event.preventDefault();

    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    if (!submitBtn || submitBtn.disabled || submitBtn.classList.contains('loading')) return;

    const variant = this.findVariant();
    const quantity = parseInt(root.querySelector('[data-cart-edit-quantity]').value, 10) || 1;
    if (!variant || !variant.available) return;

    submitBtn.classList.add('loading');
    submitBtn.setAttribute('aria-disabled', 'true');

    const removeBody = JSON.stringify({
      line: parseInt(this.lineIndex, 10),
      quantity: 0,
    });

    fetch(this.routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: removeBody,
    })
      .then((response) => response.json())
      .then(() => {
        return fetch(this.routes.cart_add_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: variant.id, quantity }],
          }),
        });
      })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || 'Unable to update cart');
        }
        this.close();
        window.location.reload();
      })
      .catch((error) => {
        console.error(error);
        this.close();
        window.location.reload();
      })
      .finally(() => {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
      });
  }
}

window.addEventListener('DOMContentLoaded', () => {
  initCollapsibleTitles();
  new CartEdit();

  if (typeof MOBILE_TITLE_MQ.addEventListener === 'function') {
    MOBILE_TITLE_MQ.addEventListener('change', () => window.requestAnimationFrame(refreshCollapsibleTitles));
  } else if (typeof MOBILE_TITLE_MQ.addListener === 'function') {
    MOBILE_TITLE_MQ.addListener(() => window.requestAnimationFrame(refreshCollapsibleTitles));
  }

  window.addEventListener('cartcount:update', () => {
    window.requestAnimationFrame(() => {
      resetCollapsibleTitlesInList();
      initCollapsibleTitles();
    });
  });
});
